// Service selector - allows switching between mock and Supabase services
// Set VITE_USE_MOCK=true in .env.local to use mock services
// Otherwise, uses Supabase services

const useMock = import.meta.env.VITE_USE_MOCK === 'true';

// Import mock services
import { mockLocationsService } from './mock-locations';
import { mockComponentsService } from './mock-components';
import { mockDisplayTypesService } from './mock-display-types';
import { mockComponentInventoryService } from './mock-component-inventory';
import { mockComponentPricingService } from './mock-component-pricing';
import { mockDisplayComponentsService } from './mock-display-components';
import { mockDisplayConfigService } from './mock-display-config';
import { mockDisplayConfigRemedyProductsService } from './mock-display-config-remedy-products';
import { mockCustomDisplayTypesService } from './mock-custom-display-types';
import { mockLocationOrdersService, mockLocationOrderItemsService } from './mock-location-orders';
import { mockLocationLaunchesService } from './mock-location-launches';
import { mockScreenHistoryService } from './mock-screen-history';
import { mockContactsService } from './mock-contacts';
import { mockNotesService } from './mock-notes';
import { mockShipmentsService } from './mock-shipments';
import { mockShipmentItemsService } from './mock-shipment-items';
import { mockPurchaseOrdersService } from './mock-purchase-orders';
import { mockPurchaseOrderItemsService } from './mock-purchase-order-items';
import { mockFulfillmentTrackingService } from './mock-fulfillment-tracking';
import { mockLaunchComponentRequirementsService } from './mock-launch-component-requirements';
import { mockDealsService } from './mock-deals';
import { mockDealDocumentsService } from './mock-deal-documents';
import { mockDealCogsEventsService } from './mock-deal-cogs-events';
import { mockCashflowEventsService } from './mock-cashflow-events';
import { mockCashflowBeginningBalancesService } from './mock-cashflow-beginning-balances';
import { mockQuotesService } from './mock-quotes';
import { mockInvoicesService } from './mock-invoices';
import { mockInvoicePaidWorkService } from './mock-invoice-paid-work';
import { mockSlaAgreementsService } from './mock-sla-agreements';
import { mockSlaAgreementSigningService } from './mock-sla-agreement-signing';
import { mockPackingSlipsService } from './mock-packing-slips';
import { mockBlueprintLeadsService } from './mock-blueprint-leads';
import { mockCustomerAccountsService } from './mock-customer-accounts';

// Import Supabase services (will be created)
import * as supabaseLocationsService from './supabase/locations';
import * as supabaseComponentsService from './supabase/components';
import * as supabaseDisplayTypesService from './supabase/display-types';
import * as supabaseComponentInventoryService from './supabase/component-inventory';
import * as supabaseComponentPricingService from './supabase/component-pricing';
import * as supabaseDisplayComponentsService from './supabase/display-components';
import * as supabaseDisplayConfigService from './supabase/display-config';
import * as supabaseDisplayConfigRemedyProductsService from './supabase/display-config-remedy-products';
import * as supabaseCustomDisplayTypesService from './supabase/custom-display-types';
import * as supabaseLocationOrdersService from './supabase/location-orders';
import * as supabaseLocationOrderItemsService from './supabase/location-order-items';
import * as supabaseLocationLaunchesService from './supabase/location-launches';
import * as supabaseScreenHistoryService from './supabase/screen-history';
import * as supabaseContactsService from './supabase/contacts';
import * as supabaseNotesService from './supabase/notes';
import * as supabaseShipmentsService from './supabase/shipments';
import * as supabaseShipmentItemsService from './supabase/shipment-items';
import * as supabasePurchaseOrdersService from './supabase/purchase-orders';
import * as supabasePurchaseOrderItemsService from './supabase/purchase-order-items';
import * as supabaseFulfillmentTrackingService from './supabase/fulfillment-tracking';
import * as supabaseLaunchComponentRequirementsService from './supabase/launch-component-requirements';
import * as supabaseDealsService from './supabase/deals';
import * as supabaseDealDocumentsService from './supabase/deal-documents';
import * as supabaseDealCogsEventsService from './supabase/deal-cogs-events';
import * as supabaseCashflowEventsService from './supabase/cashflow-events';
import * as supabaseCashflowBeginningBalancesService from './supabase/cashflow-beginning-balances';
import * as supabaseQuotesService from './supabase/quotes';
import * as supabaseInvoicesService from './supabase/invoices';
import * as supabaseInvoicePaidWorkService from './supabase/invoice-paid-work';
import * as supabaseSlaAgreementsService from './supabase/sla-agreements';
import * as supabaseSlaAgreementSigningService from './supabase/sla-agreement-signing';
import * as supabasePackingSlipsService from './supabase/packing-slips';
import * as supabaseStorePulseService from './supabase/store-pulse';
import * as supabaseBlueprintLeadsService from './supabase/blueprint-leads';
import * as supabaseCustomerAccountsService from './supabase/customer-accounts';
import * as supabaseLeadsService from './supabase/leads';
import * as supabaseOutreachSuppressionService from './supabase/outreach-suppression';
import * as supabaseOutreachSafetyService from './supabase/outreach-safety';
import * as supabaseOutreachExternalSyncService from './supabase/outreach-external-sync';
import * as supabaseProofMetricsService from './supabase/proof-metrics';
import * as supabaseProofPickerService from './supabase/proof-picker';
import * as supabaseReviewDueService from './supabase/review-due';
import * as supabaseLocationCampaignImproveItemsService from './supabase/location-campaign-improve-items';
import * as supabasePlacementAuditsService from './supabase/placement-audits';
import * as supabaseLocationReadinessGatesService from './supabase/location-readiness-gates';
import * as supabaseDealMapMilestonesService from './supabase/deal-map-milestones';
import * as supabaseDealWonHandoffService from './supabase/deal-won-handoff';
import * as supabaseLocationAtRiskFlagsService from './supabase/location-at-risk-flags';
import * as supabaseContentQueueService from './supabase/content-queue';
import * as supabaseProspectNurtureService from './supabase/prospect-nurture';
import * as supabaseLeadOutreachService from './supabase/lead-outreach';
import * as supabaseLeadListsService from './supabase/lead-lists';
import * as supabaseContactVaultService from './supabase/contact-vault';
import * as supabaseLeadIntakeService from './supabase/lead-intake';
import * as supabaseLeadOperatorsService from './supabase/lead-operators';
import {
  applyDevCustomerOpsLocationOverlay,
  findDevCustomerOpsLocationById,
  mergeDevCustomerOpsLocations,
  updateDevCustomerOpsLocation,
} from './dev-customer-ops-locations';
import { createDevReviewDueServiceWrapper } from './dev-customer-ops-review-due';
import { createDevAtRiskFlagsServiceWrapper } from './dev-customer-ops-at-risk';
import { createDevContentQueueServiceWrapper } from './dev-customer-ops-content';
import { createDevLocationReadinessGatesServiceWrapper } from './dev-customer-ops-readiness-gates';
import { createDevLocationCampaignImproveItemsServiceWrapper } from './dev-customer-ops-campaign-improve';
import { createDevPlacementAuditsServiceWrapper } from './dev-customer-ops-placement-audits';

const useDevCustomerOpsOverlay = import.meta.env.DEV
  && !useMock
  && import.meta.env.VITE_DISABLE_CUSTOMER_OPS_DEV_OVERLAY !== 'true';

function withDevCustomerOpsLocationOverlay(
  service: typeof supabaseLocationsService.locationsService
): typeof supabaseLocationsService.locationsService {
  if (!useDevCustomerOpsOverlay) {
    return service;
  }

  return {
    ...service,
    async getAll() {
      return mergeDevCustomerOpsLocations(await service.getAll());
    },
    async getById(id: string) {
      const devLocation = findDevCustomerOpsLocationById(id);
      if (devLocation) {
        return devLocation;
      }

      const location = await service.getById(id);
      if (location) {
        return applyDevCustomerOpsLocationOverlay(location);
      }
      return null;
    },
    async update(id, data) {
      const devLocation = findDevCustomerOpsLocationById(id)
        ? updateDevCustomerOpsLocation(id, data)
        : null;
      if (devLocation) {
        return devLocation;
      }

      const location = await service.getById(id);
      if (location) {
        return applyDevCustomerOpsLocationOverlay(await service.update(id, data));
      }

      return service.update(id, data);
    },
  };
}

// Export unified service interfaces
export const locationsService = useMock
  ? mockLocationsService
  : withDevCustomerOpsLocationOverlay(supabaseLocationsService.locationsService);
export const componentsService = useMock ? mockComponentsService : supabaseComponentsService.componentsService;
export const displayTypesService = useMock ? mockDisplayTypesService : supabaseDisplayTypesService.displayTypesService;
export const componentInventoryService = useMock ? mockComponentInventoryService : supabaseComponentInventoryService.componentInventoryService;
export const componentPricingService = useMock ? mockComponentPricingService : supabaseComponentPricingService.componentPricingService;
export const displayComponentsService = useMock ? mockDisplayComponentsService : supabaseDisplayComponentsService.displayComponentsService;
export const displayConfigService = useMock ? mockDisplayConfigService : supabaseDisplayConfigService.displayConfigService;
export const displayConfigRemedyProductsService = useMock
  ? mockDisplayConfigRemedyProductsService
  : supabaseDisplayConfigRemedyProductsService.displayConfigRemedyProductsService;
export const customDisplayTypesService = useMock ? mockCustomDisplayTypesService : supabaseCustomDisplayTypesService.customDisplayTypesService;
export const locationOrdersService = useMock ? mockLocationOrdersService : supabaseLocationOrdersService.locationOrdersService;
export const locationOrderItemsService = useMock ? mockLocationOrderItemsService : supabaseLocationOrderItemsService.locationOrderItemsService;
export const locationLaunchesService = useMock ? mockLocationLaunchesService : supabaseLocationLaunchesService.locationLaunchesService;
export const screenHistoryService = useMock ? mockScreenHistoryService : supabaseScreenHistoryService.screenHistoryService;
export const contactsService = useMock ? mockContactsService : supabaseContactsService.contactsService;
export const notesService = useMock ? mockNotesService : supabaseNotesService.notesService;
export const shipmentsService = useMock ? mockShipmentsService : supabaseShipmentsService.shipmentsService;
export const shipmentItemsService = useMock ? mockShipmentItemsService : supabaseShipmentItemsService.shipmentItemsService;
export const purchaseOrdersService = useMock ? mockPurchaseOrdersService : supabasePurchaseOrdersService.purchaseOrdersService;
export const purchaseOrderItemsService = useMock ? mockPurchaseOrderItemsService : supabasePurchaseOrderItemsService.purchaseOrderItemsService;
export const fulfillmentTrackingService = useMock ? mockFulfillmentTrackingService : supabaseFulfillmentTrackingService.fulfillmentTrackingService;
export const launchComponentRequirementsService = useMock
  ? mockLaunchComponentRequirementsService
  : supabaseLaunchComponentRequirementsService.launchComponentRequirementsService;
export const dealsService = useMock ? mockDealsService : supabaseDealsService.dealsService;
export const dealDocumentsService = useMock
  ? mockDealDocumentsService
  : supabaseDealDocumentsService.dealDocumentsService;
export const dealCogsEventsService = useMock ? mockDealCogsEventsService : supabaseDealCogsEventsService.dealCogsEventsService;
export const cashflowEventsService: typeof supabaseCashflowEventsService.cashflowEventsService = useMock ? mockCashflowEventsService : supabaseCashflowEventsService.cashflowEventsService;
export const cashflowBeginningBalancesService: typeof supabaseCashflowBeginningBalancesService.cashflowBeginningBalancesService =
  useMock
    ? mockCashflowBeginningBalancesService
    : supabaseCashflowBeginningBalancesService.cashflowBeginningBalancesService;
export const quotesService = useMock ? mockQuotesService : supabaseQuotesService.quotesService;
export const invoicesService = useMock ? mockInvoicesService : supabaseInvoicesService.invoicesService;
export const invoicePaidWorkService = useMock
  ? mockInvoicePaidWorkService
  : supabaseInvoicePaidWorkService.invoicePaidWorkService;
export const customerAccountsService = useMock
  ? mockCustomerAccountsService
  : supabaseCustomerAccountsService.customerAccountsService;
export const slaAgreementsService = useMock ? mockSlaAgreementsService : supabaseSlaAgreementsService.slaAgreementsService;
export const slaAgreementSigningService = useMock
  ? mockSlaAgreementSigningService
  : supabaseSlaAgreementSigningService.slaAgreementSigningService;
export const packingSlipsService = useMock
  ? mockPackingSlipsService
  : supabasePackingSlipsService.packingSlipsService;
export const blueprintLeadsService = useMock
  ? mockBlueprintLeadsService
  : supabaseBlueprintLeadsService.blueprintLeadsService;

// Lead Engine — discovered dispensary prospects (no mock; always Supabase, written by the skill)
export const leadsService = supabaseLeadsService.leadsService;
export const outreachSuppressionService = supabaseOutreachSuppressionService.outreachSuppressionService;
export const outreachSafetyService = supabaseOutreachSafetyService.outreachSafetyService;
export const outreachExternalSyncService = supabaseOutreachExternalSyncService.outreachExternalSyncService;

// Customer Ops (no mock — always Supabase)
export const proofMetricsService = supabaseProofMetricsService.proofMetricsService;
export const proofPickerService = supabaseProofPickerService.proofPickerService;
export const reviewDueService = useDevCustomerOpsOverlay
  ? createDevReviewDueServiceWrapper(supabaseReviewDueService.reviewDueService)
  : supabaseReviewDueService.reviewDueService;
export const locationAtRiskFlagsService = useDevCustomerOpsOverlay
  ? createDevAtRiskFlagsServiceWrapper(supabaseLocationAtRiskFlagsService.locationAtRiskFlagsService)
  : supabaseLocationAtRiskFlagsService.locationAtRiskFlagsService;
export const prospectNurtureService = supabaseProspectNurtureService.prospectNurtureService;
export const leadOutreachService = supabaseLeadOutreachService.leadOutreachService;
export const leadListsService = supabaseLeadListsService.leadListsService;
export const contactVaultService = supabaseContactVaultService.contactVaultService;
export const leadIntakeService = supabaseLeadIntakeService.leadIntakeService;
export const leadOperatorsService = supabaseLeadOperatorsService.leadOperatorsService;
export const contentQueueService = useDevCustomerOpsOverlay
  ? createDevContentQueueServiceWrapper(supabaseContentQueueService.contentQueueService)
  : supabaseContentQueueService.contentQueueService;
export const dealMapMilestonesService = supabaseDealMapMilestonesService.dealMapMilestonesService;
export const dealWonHandoffService = supabaseDealWonHandoffService.dealWonHandoffService;
export const locationReadinessGatesService = useDevCustomerOpsOverlay
  ? createDevLocationReadinessGatesServiceWrapper(
      supabaseLocationReadinessGatesService.locationReadinessGatesService
    )
  : supabaseLocationReadinessGatesService.locationReadinessGatesService;
export const locationCampaignImproveItemsService = useDevCustomerOpsOverlay
  ? createDevLocationCampaignImproveItemsServiceWrapper(
      supabaseLocationCampaignImproveItemsService.locationCampaignImproveItemsService
    )
  : supabaseLocationCampaignImproveItemsService.locationCampaignImproveItemsService;
export const placementAuditsService = useDevCustomerOpsOverlay
  ? createDevPlacementAuditsServiceWrapper(supabasePlacementAuditsService.placementAuditsService)
  : supabasePlacementAuditsService.placementAuditsService;

// Store Pulse services (no mock versions yet)
export const storeReportsService = supabaseStorePulseService.storeReportsService;
export const storeEmailRecipientsService = supabaseStorePulseService.storeEmailRecipientsService;
export const storeNotificationSettingsService = supabaseStorePulseService.storeNotificationSettingsService;
export const storeNameMatchingService = supabaseStorePulseService.storeNameMatchingService;
export const storeScopeReportingService = supabaseStorePulseService.storeScopeReportingService;
export const storeEmailDeliveryStatusService = supabaseStorePulseService.storeEmailDeliveryStatusService;
export const storesService = supabaseStorePulseService.storesService;
export const resolveLocationStoreName = supabaseStorePulseService.resolveLocationStoreName;

// Export types from Supabase services
export type { Contact, ContactInsert, ContactUpdate } from './supabase/contacts';
export type { Note, NoteInsert, NoteUpdate } from './supabase/notes';
export type {
  InvoicePaidWorkItem,
  InvoicePaidWorkItemEvent,
  InvoicePaidWorkRoute,
  InvoicePaidWorkStatus,
  InvoiceLocationProvisioningInput,
  InvoiceLocationProvisioningResult,
  InvoiceEquipmentResolution,
  InvoicePaidEquipmentItem,
  InvoicePaidEquipmentPlan,
  InvoicePaidEquipmentResult,
} from '@/types/invoice-paid-work';
export type {
  ProofMetric,
  ProofMetricInsert,
  ProofMetricUpdate,
  ProofMetricListFilters,
  ReviewDue,
  ReviewDueInsert,
  ReviewDueUpdate,
  ReviewDueListFilters,
  LocationAtRiskFlag,
  LocationAtRiskFlagInsert,
  LocationAtRiskFlagUpdate,
  AtRiskFlagListFilters,
  ContentQueueItem,
  ContentQueueInsert,
  ContentQueueUpdate,
  ContentQueueListFilters,
} from '@/types/customer-ops';
export type {
  NurtureStage,
  ProspectNurtureEntityType,
  ProspectNurtureEventType,
  ProspectNurtureFields,
  ProspectNurtureFieldsUpdate,
  ProspectNurtureSendEvent,
  ProspectNurtureSendEventInsert,
} from '@/types/prospect-nurture';
